// React default import kept: this repo uses the classic JSX runtime, so JSX
// below needs `React` in scope. FC/PropsWithChildren are type-only.
import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { FC, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import Popup from '@components/Popup'
import Button from '@components/Button'

/**
 * Promise-based in-app confirmation dialog.
 *
 * Replaces native `window.confirm()`, which browsers silently suppress after a
 * few dialogs ("Prevent this page from creating additional dialogs") and block
 * entirely inside the MS Teams iframe. When suppressed, `window.confirm()`
 * returns `false`, which silently broke every confirm-gated action (closing the
 * workflow/role editors, "Select All" permissions, bypass warnings).
 *
 * Usage:
 *   const confirm = useConfirm()
 *   if (await confirm({ message: '…' })) { … }
 */

export interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false))

export const useConfirm = (): ConfirmFn => useContext(ConfirmContext)

export const ConfirmProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('common')
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  // The pending Promise resolver. Kept in a ref (not state) so settle() never
  // reads a stale closure and we don't resolve inside a setState updater.
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      // Supersede any in-flight confirm: resolve its awaiter as false so it
      // doesn't hang forever, then show the new dialog. (Only one dialog is
      // rendered at a time.)
      resolverRef.current?.(false)
      resolverRef.current = resolve
      setOptions(opts)
    })
  }, [])

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value)
    resolverRef.current = null
    setOptions(null)
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <Popup onClose={() => settle(false)} className="w-[450px]" title={options.title}>
          <p className="text-lg whitespace-pre-line">{options.message}</p>
          <div className="flex justify-end w-full gap-1.5 mt-5">
            <Button color="white" className="border" onClick={() => settle(false)}>
              <span>{options.cancelLabel ?? t('cancel')}</span>
            </Button>
            <Button color="success" onClick={() => settle(true)}>
              <span>{options.confirmLabel ?? t('confirm')}</span>
            </Button>
          </div>
        </Popup>
      )}
    </ConfirmContext.Provider>
  )
}

export default ConfirmProvider
